import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { X, Send, ShieldAlert, Check, Clock, Edit2, Lock, EyeOff, ShieldCheck, Lightbulb, MapPin } from 'lucide-react';
import { Match, MatchMessage, Player, Team, UserProfile, Tournament } from '../../types';
import { collection, query, where, orderBy, onSnapshot, addDoc, updateDoc, setDoc, doc, getDoc, getDocs, writeBatch, limitToLast } from 'firebase/firestore';
import { db, getCollectionPath } from '../../lib/firebase';
import { handleFirestoreError } from '../../lib/errorHandlers';
import { formatPlayerName } from '../../lib/utils';
import { AdminPendingMatchCard } from '../admin/AdminPendingMatchCard';
import { ScorePicker } from '../common/ScorePicker';
import { RankingService } from '../../services/rankingService';
import { StatsService } from '../../services/statsService';
import { TournamentService } from '../../services/tournamentService';
import { useBackButton } from '../../hooks/useBackButton';
import { getMatchTemporalState } from '../../lib/matchmakingUtils';

interface Props {
  match: Match;
  players: Player[];
  user: UserProfile | null;
  onClose: () => void;
  isAdmin: boolean;
  isOnline?: boolean;
  initialMode?: 'chat' | 'result';
  isSuspended?: boolean;
}

const formatMessageDateTime = (timestamp: number) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterday = today - 24 * 60 * 60 * 1000;
  const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  
  const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  
  if (msgDate === today) {
    return `Hoy, ${timeStr}`;
  } else if (msgDate === yesterday) {
    return `Ayer, ${timeStr}`;
  } else {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year} - ${timeStr}`;
  }
};

export function MatchDetailModal({ match: initialMatch, players, user, onClose, isAdmin, isOnline = true, initialMode = 'chat', isSuspended = false }: Props) {
  useBackButton(true, onClose);
  
  const [match, setMatch] = useState<Match>(initialMatch);
  const [t1, setT1] = useState<Team | undefined>(undefined);
  const [t2, setT2] = useState<Team | undefined>(undefined);

  // Self-load teams for this match
  useEffect(() => {
    const loadTeams = async () => {
      const ids = [initialMatch.team1Id, initialMatch.team2Id].filter(Boolean);
      if (ids.length === 0) return;
      
      const { getDocs, query, collection, where, doc, getDoc, documentId } = await import('firebase/firestore');
      const snap = await getDocs(query(collection(db, getCollectionPath('teams')), where(documentId(), 'in', ids)));
      let teamsObj = snap.docs.map(d => ({ id: d.id, ...d.data() } as Team));
      
      let t1Obj = teamsObj.find(t => t.id === initialMatch.team1Id);
      let t2Obj = teamsObj.find(t => t.id === initialMatch.team2Id);

      // Si no los encontró en 'teams', buscarlos en los equipos registrados del torneo (si es un torneo)
      if ((!t1Obj || !t2Obj) && initialMatch.tournamentId) {
        const tSnap = await getDoc(doc(db, getCollectionPath('tournaments'), initialMatch.tournamentId));
        if (tSnap.exists()) {
          const tData = tSnap.data() as Tournament;
          if ((tData as any).registeredTeams) {
            if (!t1Obj && initialMatch.team1Id) t1Obj = (tData as any).registeredTeams.find((rt: any) => rt.id === initialMatch.team1Id);
            if (!t2Obj && initialMatch.team2Id) t2Obj = (tData as any).registeredTeams.find((rt: any) => rt.id === initialMatch.team2Id);
          }
        }
      }

      setT1(t1Obj);
      setT2(t2Obj);
    };
    loadTeams();
  }, [initialMatch.team1Id, initialMatch.team2Id]);

  const [messages, setMessages] = useState<MatchMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isReporting, setIsReporting] = useState(initialMode === 'result' && !initialMatch.reportedResult);
  const [isImpugning, setIsImpugning] = useState(false);
  const [impugnReason, setImpugnReason] = useState('');
  const [s1, setS1] = useState('');
  const [s2, setS2] = useState('');
  const [s3, setS3] = useState('');
  const [winnerTeamId, setWinnerTeamId] = useState<string>('');
  const [currentTime, setCurrentTime] = useState(Date.now());
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [limitCount, setLimitCount] = useState(15);
  const [hasMore, setHasMore] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInitialRef = useRef(true);
  const isPaginatingRef = useRef(false);
  const scrollHeightRef = useRef(0);
  const prevFirstIdRef = useRef<string | undefined>(undefined);
  const prevMessagesLengthRef = useRef(0);

  // Reset when match.id changes
  useEffect(() => {
    isInitialRef.current = true;
    setLimitCount(15);
    setHasMore(true);
    prevFirstIdRef.current = undefined;
    prevMessagesLengthRef.current = 0;
  }, [match.id]);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, getCollectionPath('matches'), initialMatch.id), (docSnap) => {
      if (docSnap.exists()) {
        setMatch({ id: docSnap.id, ...docSnap.data() } as Match);
      }
    });
    return () => unsubscribe();
  }, [initialMatch.id]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);

  const [tournament, setTournament] = useState<Tournament | null>(null);

  useEffect(() => {
    if (!match.tournamentId) {
      setTournament(null);
      return;
    }
    const unsubscribe = onSnapshot(doc(db, getCollectionPath('tournaments'), match.tournamentId), (docSnap) => {
      if (docSnap.exists()) {
        setTournament({ id: docSnap.id, ...docSnap.data() } as Tournament);
      }
    });
    return () => unsubscribe();
  }, [match.tournamentId]);

  const [sedes, setSedes] = useState<any[]>([]);
  const [showReserveClubs, setShowReserveClubs] = useState(false);

  useEffect(() => {
    const loadSedes = async () => {
      if (!tournament?.locationClubIds || tournament.locationClubIds.length === 0) return;
      const { getDocs, query, collection, where, documentId } = await import('firebase/firestore');
      const snap = await getDocs(query(collection(db, getCollectionPath('users')), where(documentId(), 'in', tournament.locationClubIds)));
      setSedes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    loadSedes();
  }, [tournament?.locationClubIds]);

  const getMatchRoundNumber = (m: Match): number => {
    if (m.roundNumber !== undefined) {
      return m.roundNumber;
    }
    if (m.id && m.tournamentId && m.id.startsWith(`bracket_${m.tournamentId}_`)) {
      const matchRegex = m.id.match(/_(\d+)_(\d+)$/);
      if (matchRegex) {
        const bIdx = parseInt(matchRegex[1], 10);
        return bIdx + 1;
      }
    }
    return 1;
  };

  const roundNumber = getMatchRoundNumber(match);
  const startedAt = tournament?.startedAt || tournament?.createdAt || match.createdAt || Date.now();
  const daysPerRound = tournament?.scheduleDaysPerRound || 7;
  const isMinutes = tournament?.scheduleTimeUnit === 'minutes';
  const isHours = tournament?.scheduleTimeUnit === 'hours';
  const timeUnitMs = isMinutes ? (60 * 1000) : isHours ? (60 * 60 * 1000) : (24 * 60 * 60 * 1000);
  
  let isFutureRoundLocked = false;
  if (!isAdmin && match.tournamentId && tournament) {
    const { matchState } = getMatchTemporalState(match, tournament);
    isFutureRoundLocked = matchState === 'inactivo';
  }

  // t1 and t2 are now loaded via useEffect above

  const isParticipant = user && (t1?.player1Id === user.uid || t1?.player2Id === user.uid || t1?.player3Id === user.uid ||
                                 t2?.player1Id === user.uid || t2?.player2Id === user.uid || t2?.player3Id === user.uid);

  const canViewChat = isAdmin || isParticipant;
  const isDisputed = match.status === 'disputed' || !!match.impugnedAt;
  const isArchivedDispute = match.status === 'completed' && !!match.disputeResolvedAt;
  
  const isSuperAdmin = user?.role === 'superadmin';
  const isNormalAdmin = ['admin', 'co_admin', 'gestor_torneos'].includes(user?.role || '');
  const isNormalAdminLocked = isNormalAdmin && !isParticipant && !isDisputed && !isArchivedDispute;
  const hasChatAccess = isParticipant || isSuperAdmin || (isNormalAdmin && (isDisputed || isArchivedDispute));

  const canSendMessage = !isSuspended && !isArchivedDispute && (isParticipant || (isAdmin && isDisputed && hasChatAccess));

  useEffect(() => {
    if (!canViewChat || !user || !hasChatAccess) {
      setMessages([]);
      return;
    }

    // Admin uses unfiltered query to see ALL messages; players filter by participantIds
    const chatId = `match_${match.id}`;
    const q = query(
      collection(db, getCollectionPath('private_chats'), chatId, 'messages'),
      orderBy('createdAt', 'asc'),
      limitToLast(limitCount)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs: MatchMessage[] = [];
      snapshot.forEach(doc => {
        msgs.push({ id: doc.id, ...doc.data() } as MatchMessage);
      });

      if (msgs.length < limitCount) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }

      setMessages(msgs);
    }, (error) => {
      console.error("Error fetching messages:", error);
    });

    return () => unsubscribe();
  }, [match.id, canViewChat, limitCount, hasChatAccess]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    if (container.scrollTop <= 5 && hasMore && !isPaginatingRef.current) {
      scrollHeightRef.current = container.scrollHeight;
      isPaginatingRef.current = true;
      setLimitCount(prev => prev + 15);
    }
  };

  useEffect(() => {
    const currentFirstId = messages[0]?.id;
    const prevFirstId = prevFirstIdRef.current;

    if (isInitialRef.current && messages.length > 0) {
      if (containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }
      isInitialRef.current = false;
    } else if (isPaginatingRef.current && containerRef.current) {
      const container = containerRef.current;
      const addedHeight = container.scrollHeight - scrollHeightRef.current;
      container.scrollTop = addedHeight;
      isPaginatingRef.current = false;
    } else if (prevFirstId && currentFirstId === prevFirstId && messages.length > prevMessagesLengthRef.current) {
      if (containerRef.current) {
        containerRef.current.scrollTo({
          top: containerRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }
    }

    prevFirstIdRef.current = currentFirstId;
    prevMessagesLengthRef.current = messages.length;
  }, [messages]);

  useEffect(() => {
    if (!user || !match.id || !canViewChat || !hasChatAccess) return;
    const chatId = `match_${match.id}`;
    const chatRef = doc(db, getCollectionPath('private_chats'), chatId);
    updateDoc(chatRef, {
      [`unreadCount.${user.uid}`]: 0
    }).catch(() => {});
  }, [match.id, user?.uid, canViewChat, hasChatAccess]);

  const handleSendMessage = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!newMessage.trim() || !user) return;

    const participantIds = [];
    if (t1) participantIds.push(t1.player1Id, t1.player2Id, t1.player3Id);
    if (t2) participantIds.push(t2.player1Id, t2.player2Id, t2.player3Id);
    
    try {
      const chatId = `match_${match.id}`;
      const messageText = newMessage.trim();
      setNewMessage('');

      const chatRef = doc(db, getCollectionPath('private_chats'), chatId);
      const chatSnap = await getDoc(chatRef);

      const pIds = participantIds.filter(Boolean) as string[];
      const pNames: Record<string, string> = {};
      const pPhotos: Record<string, string> = {};

      pIds.forEach(id => {
        const p = players.find(player => player.id === id);
        if (p) {
          pNames[id] = p.name;
          pPhotos[id] = p.photoUrl || '';
        }
      });

      const unreadCount: Record<string, number> = chatSnap.exists() ? (chatSnap.data().unreadCount || {}) : {};
      pIds.forEach(id => {
        if (id !== user.uid) {
          unreadCount[id] = (unreadCount[id] || 0) + 1;
        } else {
          unreadCount[id] = 0;
        }
      });

      const matchContext = match.round 
        ? `${match.round}${match.tournamentName ? ` - ${match.tournamentName}` : ''}`
        : (match.tournamentName || 'Partido');

      const chatData: any = {
        participantIds: pIds,
        participantNames: pNames,
        participantPhotos: pPhotos,
        updatedAt: Date.now(),
        lastMessage: messageText,
        lastMessageSenderId: user.uid,
        unreadCount,
        type: 'group',
        matchId: match.id,
        title: `Chat grupal. ${matchContext}`
      };

      if (!chatSnap.exists()) {
        await setDoc(chatRef, chatData);
      } else {
        await updateDoc(chatRef, chatData);
      }

      await addDoc(collection(db, getCollectionPath('private_chats'), chatId, 'messages'), {
        matchId: match.id,
        senderId: user.uid,
        text: messageText,
        createdAt: Date.now(),
        participantIds: pIds
      });
    } catch (error) {
      if (error instanceof Error && error.message.includes('Missing or insufficient permissions')) {
         handleFirestoreError(error, 'create' as any, `matches/${match.id}/messages`);
      } else {
        console.error("Error sending message:", error);
      }
    }
  };

  const submitResult = async () => {
    if (!winnerTeamId || !s1 || !s2) return;
    try {
      const participantIds = [];
      if (t1) participantIds.push(t1.player1Id, t1.player2Id, t1.player3Id);
      if (t2) participantIds.push(t2.player1Id, t2.player2Id, t2.player3Id);
      const filteredParticipants = participantIds.filter(Boolean) as string[];
      
      const payload: any = {
        reportedResult: { s1, s2, s3, winnerId: winnerTeamId },
        reportedById: user?.uid,
        reportedAt: Date.now(),
      };

      // Also ensure participantIds exists on the match for future rules
      if (!match.participantIds) {
          payload.participantIds = filteredParticipants;
      }

      await updateDoc(doc(db, getCollectionPath('matches'), match.id), payload);
      setIsReporting(false);
      
      const myTeam = (t1 && (t1.player1Id === user?.uid || t1.player2Id === user?.uid || t1.player3Id === user?.uid)) ? 't1' : 't2';
      const otherParticipants = filteredParticipants.filter(id => id !== user?.uid);
      const scoreStr = [s1, s2, s3].filter(Boolean).join(', ');
      const winnerNameStr = winnerTeamId === t1?.id ? (t1?.name || 'T1') : (t2?.name || 'T2');
      
      for (const pid of otherParticipants) {
        const isTeammate = myTeam === 't1' 
          ? (t1 && (t1.player1Id === pid || t1.player2Id === pid || t1.player3Id === pid))
          : (t2 && (t2.player1Id === pid || t2.player2Id === pid || t2.player3Id === pid));
          
        const customBodyStr = isTeammate 
          ? `Tu compañero ha cargado el resultado: gana ${winnerNameStr} ${scoreStr}. Pendiente de confirmación del rival.` 
          : `El equipo rival ha cargado el resultado: gana ${winnerNameStr} ${scoreStr}. ¡Acéptalo para confirmar el partido!`;
          
        await addDoc(collection(db, getCollectionPath('notifications')), {
          userId: pid, type: 'match_reported', title: 'Resultado Cargado',
          body: customBodyStr,
          matchId: match.id, read: false, createdAt: Date.now()
        });
      }
    } catch (error) {
      console.error("Error reporting result:", error);
      alert("Error reportando el resultado.");
    }
  };

  const acceptResult = async () => {
    if (!match.reportedResult) return;
    try {
      alert("¡Resultado confirmado!");
      onClose();

      const qNotifs = query(collection(db, getCollectionPath('notifications')), where('matchId', '==', match.id), where('userId', '==', user?.uid));
      const snapNotifs = await getDocs(qNotifs);
      snapNotifs.forEach(docN => { updateDoc(docN.ref, { actionTaken: true, read: true }).catch(console.error); });
      const scoreStr = [match.reportedResult.s1, match.reportedResult.s2, match.reportedResult.s3]
        .filter(Boolean)
        .map(s => `[${s}]`)
        .join(' ');

      const pIds = match.participantIds || [];
      if (pIds.length === 0) {
        if (t1) pIds.push(t1.player1Id, t1.player2Id, t1.player3Id);
        if (t2) pIds.push(t2.player1Id, t2.player2Id, t2.player3Id);
      }
      const winnerPlayerIds = match.reportedResult.winnerId === t1?.id 
        ? [t1?.player1Id, t1?.player2Id, t1?.player3Id].filter(Boolean)
        : match.reportedResult.winnerId === t2?.id 
          ? [t2?.player1Id, t2?.player2Id, t2?.player3Id].filter(Boolean)
          : [];

      const payload: any = {
        status: 'completed',
        s1: match.reportedResult.s1,
        s2: match.reportedResult.s2,
        s3: match.reportedResult.s3,
        score: scoreStr,
        winnerId: match.reportedResult.winnerId,
        participantIds: pIds.filter(Boolean),
        winnerPlayerIds,
        team1Obj: t1 || null,
        team2Obj: t2 || null
      };

      if (match.status === 'disputed' || match.impugnedAt) {
        payload.disputeResolvedAt = Date.now();
        payload.disputeResolvedById = user?.uid;
      }

      await updateDoc(doc(db, getCollectionPath('matches'), match.id), payload);

      // Update ranking points
      const updatedMatch = { ...match, ...payload } as Match;
      const pointsMap = await RankingService.processMatchPoints(updatedMatch, true);
      StatsService.processMatchStats(updatedMatch).catch(e => console.error("Error updating player stats", e));

      await TournamentService.updateTournamentAfterMatch(
        updatedMatch,
        match.reportedResult?.s1 || '',
        match.reportedResult?.s2 || '',
        match.reportedResult?.s3 || '',
        match.reportedResult?.winnerId || ''
      );
      
      setIsReporting(false);
      
      const wasDisputed = match.status === 'disputed' || !!match.impugnedAt;
      const resultStr = [match.reportedResult.s1, match.reportedResult.s2, match.reportedResult.s3].filter(Boolean).join(', ');
      
      const winnerName = match.reportedResult.winnerId === t1?.id ? t1?.name : (match.reportedResult.winnerId === t2?.id ? t2?.name : 'Ganador');
      const allParticipants = pIds.filter((id: any) => id);
      
      for (const pid of allParticipants) {
        if (pid) {
          const pointsEarned = pointsMap[pid] || 0;
          const pointsText = pointsEarned > 0 ? ` Has sumado +${pointsEarned} pts al ranking.` : '';

          await addDoc(collection(db, getCollectionPath('notifications')), {
            userId: pid, type: 'match_reported',
            title: wasDisputed ? 'Disputa Resuelta' : 'Resultado Aceptado',
            body: wasDisputed
              ? `El administrador ha finalizado la disputa. Ganador: ${winnerName} (${resultStr}).${pointsText}`
              : `¡El partido ha finalizado! Ganador: ${winnerName} (${resultStr}).${pointsText}`,
            matchId: match.id, read: false, createdAt: Date.now(),
            ...(pointsEarned > 0 ? { points: pointsEarned } : {})
          });
        }
      }

    } catch (error) {
      console.error("Error accepting result:", error);
    }
  };

  const impugnResult = async () => {
    if (!impugnReason.trim()) {
      alert("Por favor, ingresa el motivo de la impugnación.");
      return;
    }
    try {
      const qNotifs = query(collection(db, getCollectionPath('notifications')), where('matchId', '==', match.id), where('userId', '==', user?.uid));
      const snapNotifs = await getDocs(qNotifs);
      snapNotifs.forEach(docN => { updateDoc(docN.ref, { actionTaken: true, read: true }).catch(console.error); });
      
      await updateDoc(doc(db, getCollectionPath('matches'), match.id), {
        status: 'disputed',
        impugnedAt: Date.now(),
        impugnedById: user?.uid,
        impugnedReason: impugnReason.trim()
      });
      const pIds = match.participantIds || [];
      if (pIds.length === 0) {
        if (t1) pIds.push(t1.player1Id, t1.player2Id, t1.player3Id);
        if (t2) pIds.push(t2.player1Id, t2.player2Id, t2.player3Id);
      }
      const myTeam = (t1 && (t1.player1Id === user?.uid || t1.player2Id === user?.uid || t1.player3Id === user?.uid)) ? 't1' : 't2';
      const otherParticipants = pIds.filter((id: any) => id && id !== user?.uid);

      // Notify all admins and superadmins
      const usersRef = collection(db, getCollectionPath('users'));
      const qAdmins = query(usersRef, where('role', 'in', ['admin', 'superadmin', 'co_admin', 'gestor_torneos']));
      const adminDocs = await getDocs(qAdmins);
      
      const batch = writeBatch(db);
      adminDocs.forEach(adminDoc => {
        if (adminDoc.id === user?.uid || otherParticipants.includes(adminDoc.id)) return;
        const notifRef = doc(collection(db, getCollectionPath('notifications')));
        batch.set(notifRef, {
          userId: adminDoc.id,
          type: 'match_impugned',
          title: `Resultado Impugnado`,
          body: `Se ha impugnado un resultado en un partido y requiere intervención.`,
          matchId: match.id,
          read: false,
          createdAt: Date.now()
        });
      });
      await batch.commit();
      for (const pid of otherParticipants) {
        if (pid) {
          const isTeammate = myTeam === 't1' 
            ? (t1 && (t1.player1Id === pid || t1.player2Id === pid || t1.player3Id === pid))
            : (t2 && (t2.player1Id === pid || t2.player2Id === pid || t2.player3Id === pid));

          await addDoc(collection(db, getCollectionPath('notifications')), {
            userId: pid, type: 'match_impugned',
            title: 'Resultado Impugnado',
            body: isTeammate 
              ? 'Tu compañero ha impugnado el resultado. Un administrador intervendrá pronto.' 
              : 'La pareja oponente ha impugnado el resultado. Un administrador intervendrá pronto.',
            matchId: match.id, read: false, createdAt: Date.now()
          });
        }
      }
    } catch (error) {
      console.error("Error impugning result:", error);
    }
  };

  const resolveDispute = async (action: 'accept_original' | 'new_result') => {
    if (action === 'accept_original') {
      await acceptResult(); // this will set status to completed and calculate pts
    } else {
      setIsReporting(true);
      // We keep status disputed until admin saves the new result.
    }
  };

  const getPlayerName = (id: string) => {
    if (id === user?.uid) return 'Tú';
    if (id === match.organizerId) return 'Administrador';
    const player = players.find(p => p.id === id);
    return player ? formatPlayerName(player.name, 'short') : 'Desconocido';
  };

  const isReportedByMyTeam = () => {
    if (!match.reportedById || !user) return false;
    if (t1?.player1Id === user.uid || t1?.player2Id === user.uid) {
      return t1.player1Id === match.reportedById || t1.player2Id === match.reportedById;
    }
    if (t2?.player1Id === user.uid || t2?.player2Id === user.uid) {
      return t2.player1Id === match.reportedById || t2.player2Id === match.reportedById;
    }
    return false;
  };

  const renderReportPanel = () => {
    if (isSuspended) {
      return (
        <div className="rounded-[24px] border border-red-500/30 bg-gradient-to-b from-red-950/25 to-black/60 p-6 flex flex-col items-center text-center shadow-[0_0_25px_rgba(239,68,68,0.1)]">
          <div className="w-14 h-14 bg-gradient-to-b from-red-500/20 to-red-500/5 rounded-2xl flex items-center justify-center mb-4 border border-red-500/30">
            <Lock size={24} className="text-red-400" />
          </div>
          <h4 className="text-xs font-black uppercase tracking-widest text-red-300 mb-1">
            Cuenta Inhabilitada
          </h4>
          <p className="text-[11px] text-white/70 leading-relaxed mb-4 uppercase">
            No posees permisos para reportar, confirmar o impugnar resultados de partidos debido a una inhabilitación activa en tu cuenta.
          </p>
          <p className="text-[9px] font-semibold text-white/40 italic leading-normal">
            Si consideras que esto es un error, por favor ponte en contacto con Soporte Técnico.
          </p>
        </div>
      );
    }

    if (!isAdmin && tournament?.managementMode === 'express') {
      return (
        <div className="relative overflow-hidden rounded-[24px] border border-gray-500/30 bg-gradient-to-b from-gray-950/40 to-black/60 p-4 sm:p-5 flex flex-col items-center text-center shadow-[0_0_25px_rgba(107,114,128,0.1)] w-full shrink-0">
          <div className="relative z-10 flex flex-col items-center w-full max-w-sm mx-auto">
            <div className="w-10 h-10 bg-gradient-to-b from-gray-500/20 to-gray-400/5 rounded-2xl flex items-center justify-center mb-3 border border-gray-500/30 shrink-0">
              <EyeOff size={16} className="text-gray-400" />
            </div>
            <p className="text-[10px] sm:text-[11px] text-white/70 leading-relaxed mb-1 uppercase text-center font-bold w-full max-w-[260px]">
              Modalidad de Organización
            </p>
            <p className="text-[9px] text-white/40 leading-relaxed uppercase text-center w-full max-w-[260px]">
              En este torneo, los resultados solo pueden ser cargados por el organizador.
            </p>
          </div>
        </div>
      );
    }

    if (isFutureRoundLocked) {
      return (
        <div className="relative overflow-hidden rounded-[24px] border border-gray-500/30 bg-gradient-to-b from-gray-950/40 to-black/60 p-4 sm:p-5 flex flex-col items-center text-center shadow-[0_0_25px_rgba(107,114,128,0.1)] w-full shrink-0">
          <div className="relative z-10 flex flex-col items-center w-full max-w-sm mx-auto">
            <div className="w-10 h-10 bg-gradient-to-b from-gray-500/20 to-gray-400/5 rounded-2xl flex items-center justify-center mb-3 border border-gray-500/30 shrink-0">
              <Lock size={16} className="text-gray-400" />
            </div>

            <p className="text-[10px] sm:text-[11px] text-white/70 leading-relaxed mb-5 uppercase text-center font-bold w-full max-w-[260px]">
              La carga de resultados solo estará <br className="hidden sm:block" /> 
              <span className="sm:hidden">habilitada en la fecha correspondiente.</span>
              <span className="hidden sm:inline">habilitada en la fecha correspondiente.</span>
            </p>

            {messages.length === 0 && (
              <div className="flex items-center gap-3 bg-yellow-500/10 border border-yellow-500/20 p-3 rounded-xl animate-pulse shadow-[0_0_15px_rgba(234,179,8,0.15)] w-full max-w-sm">
                <div className="flex items-center gap-1.5 bg-yellow-400/20 px-2 py-1 rounded-lg shrink-0 border border-yellow-400/30">
                  <Lightbulb size={12} className="text-yellow-400" />
                  <span className="font-black tracking-widest text-yellow-400 text-[9px] uppercase mt-[1px]">TIP</span>
                </div>
                <p className="text-[9.5px] sm:text-[10px] text-yellow-200/90 leading-tight text-left font-medium">
                  Mientras tanto, puedes ir coordinando el partido con tus rivales por este chat!
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    const hasReported = !!match.reportedResult;
    const myTeamReported = isReportedByMyTeam();
    const isOpponent = isParticipant && !myTeamReported;

    // Shared result display card
    const renderResultCard = () => {
      if (!hasReported) return null;
      const timePassed = currentTime - (match.reportedAt || 0);
      const msLeft = (24 * 60 * 60 * 1000) - timePassed;
      const hoursLeft = Math.max(0, Math.floor(msLeft / (1000 * 60 * 60)));
      const minsLeft = Math.max(0, Math.floor((msLeft % (1000 * 60 * 60)) / (1000 * 60)));
      const isExpired = msLeft <= 0;
      const showTimer = match.status === 'pending';
      const isDisputedMatch = match.status === 'disputed' || !!match.impugnedAt;
      const isComplete = match.status === 'completed';

      // Determine the winner team name
      const winnerTeam = match.reportedResult?.winnerId === t1?.id ? t1 : t2;
      const winnerNames = winnerTeam ? `${formatPlayerName(players.find(p => p.id === winnerTeam.player1Id)?.name || '', 'short')} / ${formatPlayerName(players.find(p => p.id === winnerTeam.player2Id)?.name || '', 'short')}` : '';

      return (
        <div className={`rounded-2xl border overflow-hidden ${isDisputedMatch ? 'border-red-500/40' : isComplete ? 'border-emerald-500/30' : 'border-white/10'}`}>
          {/* Status bar */}
          <div className={`px-4 py-2 flex items-center justify-between ${isComplete ? 'bg-emerald-500/20' : isDisputedMatch ? 'bg-red-500/20' : 'bg-white/5 border-b border-white/10'}`}>
            <div className="flex items-center gap-2">
              {isComplete && <Check size={14} className="text-emerald-400" />}
              {isDisputedMatch && <ShieldAlert size={14} className="text-red-400" />}
              {!isComplete && !isDisputedMatch && <Clock size={14} className="text-amber-400" />}
              <span className={`text-[11px] font-black uppercase tracking-widest ${isComplete ? 'text-emerald-400' : isDisputedMatch ? 'text-red-400' : 'text-amber-400'}`}>
                {isComplete ? 'Finalizado' : isDisputedMatch ? (isAdmin ? 'En Revisión' : 'En Disputa') : 'Esperando Confirmación'}
              </span>
            </div>
            {showTimer && !isExpired && (
              <div className="flex items-center gap-1.5">
                <Clock size={11} className="text-amber-400 animate-pulse" />
                <span className="text-[11px] font-mono font-bold text-amber-400">{hoursLeft}h {minsLeft}m</span>
              </div>
            )}
            {showTimer && isExpired && (
              <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider">Plazo vencido</span>
            )}
          </div>

          {/* Scores */}
          <div className="p-4 bg-black/30">
            <div className="flex items-center justify-center gap-3">
              {[match.reportedResult?.s1, match.reportedResult?.s2, match.reportedResult?.s3].filter(Boolean).map((score, i) => (
                <div key={i} className="w-14 h-10 flex items-center justify-center bg-black/50 rounded-xl border border-white/15 font-black text-base text-white tracking-wide">
                  {score}
                </div>
              ))}
            </div>
            {winnerNames && (
              <p className="text-center text-[10px] text-white/40 mt-2 uppercase tracking-wider">
                Ganador: <span className="text-white/70 font-bold">{winnerNames}</span>
              </p>
            )}
          </div>

          {/* Actions footer */}
          <div className="px-4 py-3 bg-black/20 border-t border-white/5">
            {/* COMPLETED: nothing to do */}
            {isComplete && (
              <p className="text-center text-[10px] text-emerald-400/70 font-bold uppercase tracking-widest">Resultado confirmado</p>
            )}

            {/* DISPUTED: admin sees resolve buttons, players see waiting msg */}
            {isDisputedMatch && !isComplete && (
              <>
                {isAdmin ? (
                  !isOnline ? (
                    <div className="px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-xl text-center">
                      <p className="text-[10px] text-amber-500 font-bold uppercase tracking-widest">⚠️ Modo Offline — Conéctate para resolver disputas</p>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={() => acceptResult()}
                        className="flex-1 py-2.5 bg-emerald-500 text-white font-black uppercase text-[10px] rounded-xl hover:bg-emerald-600 transition-all shadow-md tracking-wider"
                      >
                        ✓ Aceptar Original
                      </button>
                      <button
                        onClick={() => setIsReporting(true)}
                        className="flex-1 py-2.5 bg-white/10 text-white border border-white/20 font-black uppercase text-[10px] rounded-xl hover:bg-white/20 transition-all tracking-wider"
                      >
                        ✎ Nuevo Resultado
                      </button>
                    </div>
                  )
                ) : (
                  <p className="text-center text-[10px] text-red-400/80 font-bold uppercase tracking-widest">Esperando resolución del administrador...</p>
                )}
              </>
            )}

            {/* PENDING: opponent can accept/impugn, reporter can modify, admin sees nothing */}
            {match.status === 'pending' && hasReported && (
              <>
                {isOpponent && !isExpired ? (
                  !isOnline ? (
                    <div className="px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-xl text-center">
                      <p className="text-[10px] text-amber-500 font-bold uppercase tracking-widest">⚠️ Modo Offline — Conéctate para confirmar resultado</p>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {!isImpugning ? (
                        <div className="flex items-center justify-center gap-1.5 mt-2 max-w-sm mx-auto w-full">
                          <button onClick={acceptResult} className="px-4 py-2 bg-[#84cc16] text-black font-black uppercase text-[10px] rounded-lg hover:bg-[#65a30d] transition-all shadow-md tracking-wider flex-1">
                            ✓ Aceptar
                          </button>
                          <button onClick={() => setIsReporting(true)} className="px-4 py-2 bg-yellow-500 text-black font-black uppercase text-[10px] rounded-lg hover:bg-yellow-400 transition-all shadow-md tracking-wider flex-1">
                            ✎ Modificar
                          </button>
                          <button onClick={() => setIsImpugning(true)} className="px-2.5 py-1.5 bg-transparent border border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50 font-bold uppercase text-[8px] rounded transition-all tracking-wider ml-1">
                            Impugnar
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-xl animate-in fade-in duration-200">
                          <p className="text-[10px] font-bold text-red-400 uppercase tracking-widest text-center mb-1">Motivo de la Impugnación</p>
                          <textarea
                            value={impugnReason}
                            onChange={(e) => setImpugnReason(e.target.value)}
                            placeholder="Explica brevemente por qué el resultado es incorrecto..."
                            className="w-full bg-black/40 text-white p-2 rounded-lg text-xs resize-none outline-none focus:ring-2 ring-red-500/50 border border-white/10"
                            rows={3}
                          />
                          <div className="flex gap-2 mt-1">
                            <button onClick={() => setIsImpugning(false)} className="flex-1 py-2 bg-white/5 text-white/60 font-black uppercase text-[9px] rounded-lg hover:bg-white/10 transition-all">
                              Cancelar
                            </button>
                            <button onClick={impugnResult} disabled={!impugnReason.trim()} className="flex-1 py-2 bg-red-500 text-white font-black uppercase text-[9px] rounded-lg hover:bg-red-600 transition-all disabled:opacity-50">
                              Enviar Impugnación
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                ) : isExpired ? (
                  !isOnline ? (
                    <div className="px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-xl text-center">
                      <p className="text-[10px] text-amber-500 font-bold uppercase tracking-widest">⚠️ Modo Offline — Conéctate para aprobar resultado</p>
                    </div>
                  ) : (
                    <button onClick={acceptResult} className="w-full py-2.5 bg-emerald-500 text-white font-black uppercase text-[10px] rounded-xl hover:bg-emerald-600 transition-all shadow-md flex items-center justify-center gap-2 tracking-wider">
                      <Check size={14} /> Aprobación Automática (Plazo Vencido)
                    </button>
                  )
                ) : myTeamReported ? (
                  <div className="flex flex-col items-center gap-2">
                    <p className="text-[10px] text-white/40 uppercase tracking-widest font-bold">Esperando confirmación del rival...</p>
                    <button onClick={() => setIsReporting(true)} className="text-[10px] text-white/50 uppercase tracking-wider font-bold hover:text-white flex items-center gap-1 transition-colors">
                      <Edit2 size={10} /> Modificar Resultado
                    </button>
                  </div>
                ) : isAdmin ? (
                  <p className="text-center text-[10px] text-white/40 font-bold uppercase tracking-widest">Resultado pendiente de confirmación entre jugadores</p>
                ) : null}

                {/* Timer message removed per user request */}
              </>
            )}
          </div>
        </div>
      );
    };

    return (
      <div className="flex flex-col gap-3">
        {/* Always show the result card if there's a reported result */}
        {renderResultCard()}

        {/* Inline edit form — shows BELOW the result card */}
        {isReporting && t1 && t2 && (!(!isAdmin && tournament?.managementMode === 'express')) && (
          <ScorePicker
            pair1={{ ids: [t1.player1Id, t1.player2Id, t1.player3Id].filter(Boolean) as string[], label: 'Pareja 1', color: 'text-emerald-400' }}
            pair2={{ ids: [t2.player1Id, t2.player2Id, t2.player3Id].filter(Boolean) as string[], label: 'Pareja 2', color: 'text-rose-400' }}
            players={players}
            onSave={async (finalS1, finalS2, finalS3) => {
              const qNotifs = query(collection(db, getCollectionPath('notifications')), where('matchId', '==', match.id), where('userId', '==', user?.uid));
              const snapNotifs = await getDocs(qNotifs);
              snapNotifs.forEach(docN => { updateDoc(docN.ref, { actionTaken: true, read: true }).catch(console.error); });
              let setsWon1 = 0; let setsWon2 = 0;
              [finalS1, finalS2, finalS3].filter(Boolean).forEach(s => {
                const [vs1, vs2] = s.split('-').map(Number);
                if (!isNaN(vs1) && !isNaN(vs2)) {
                  if (vs1 > vs2) setsWon1++;
                  else if (vs2 > vs1) setsWon2++;
                }
              });
              const winnerId = setsWon1 > setsWon2 ? t1.id : t2.id;

              const participantIds: string[] = [];
              if (t1) participantIds.push(t1.player1Id, t1.player2Id, t1.player3Id);
              if (t2) participantIds.push(t2.player1Id, t2.player2Id, t2.player3Id);
              const filteredParticipants = participantIds.filter(Boolean) as string[];

              if (isAdmin && (!isParticipant || tournament?.managementMode === 'express')) {
                const scoreStr = [finalS1, finalS2, finalS3].filter(Boolean).map(s => `[${s}]`).join(' ');
                const wasDisputed = match.status === 'disputed' || !!match.impugnedAt;
                const payload: any = {
                  status: 'completed', s1: finalS1, s2: finalS2, s3: finalS3,
                  score: scoreStr, winnerId,
                  reportedResult: { s1: finalS1, s2: finalS2, s3: finalS3, winnerId },
                  reportedById: user?.uid, reportedAt: Date.now()
                };
                if (wasDisputed) {
                  payload.disputeResolvedAt = Date.now();
                  payload.disputeResolvedById = user?.uid;
                }
                if (!match.participantIds) payload.participantIds = filteredParticipants;
                await updateDoc(doc(db, getCollectionPath('matches'), match.id), payload);

                // Update ranking points
                const updatedMatch = { ...match, ...payload } as Match;
                await RankingService.processMatchPoints(updatedMatch);
                StatsService.processMatchStats(updatedMatch).catch(e => console.error("Error updating player stats", e));

                await TournamentService.updateTournamentAfterMatch(
                  updatedMatch,
                  finalS1,
                  finalS2,
                  finalS3,
                  winnerId
                );

                // Notify participants
                const resultText = [finalS1, finalS2, finalS3].filter(Boolean).join(', ');
                const otherParticipants = filteredParticipants.filter(id => id !== user?.uid);
                for (const pid of otherParticipants) {
                  if (pid) {
                    await addDoc(collection(db, getCollectionPath('notifications')), {
                      userId: pid, type: 'match_reported',
                      title: wasDisputed ? 'Disputa Resuelta' : 'Resultado Confirmado',
                      body: wasDisputed
                        ? `El administrador ha finalizado la disputa con un nuevo resultado: ${resultText}`
                        : `El administrador ha confirmado el resultado: ${resultText}`,
                      matchId: match.id, read: false, createdAt: Date.now()
                    });
                  }
                }
              } else {
                const payload: any = {
                  reportedResult: { s1: finalS1, s2: finalS2, s3: finalS3, winnerId },
                  reportedById: user?.uid, reportedAt: Date.now()
                };
                if (!match.participantIds) payload.participantIds = filteredParticipants;
                await updateDoc(doc(db, getCollectionPath('matches'), match.id), payload);

                const otherParticipants = filteredParticipants.filter(id => id !== user?.uid);
                const scoreStr = [finalS1, finalS2, finalS3].filter(Boolean).join(', ');
                const winnerNameStr = winnerId === t1?.id ? (t1?.name || 'T1') : (t2?.name || 'T2');
                const myTeam = (t1 && (t1.player1Id === user?.uid || t1.player2Id === user?.uid || t1.player3Id === user?.uid)) ? 't1' : 't2';

                for (const pid of otherParticipants) {
                  if (pid) {
                    const isTeammate = myTeam === 't1' 
                      ? (t1 && (t1.player1Id === pid || t1.player2Id === pid || t1.player3Id === pid))
                      : (t2 && (t2.player1Id === pid || t2.player2Id === pid || t2.player3Id === pid));
                      
                    const customBodyStr = isTeammate 
                      ? `Tu compañero ha cargado el resultado: gana ${winnerNameStr} ${scoreStr}. Pendiente de confirmación del rival.` 
                      : `El equipo rival ha cargado el resultado: gana ${winnerNameStr} ${scoreStr}. ¡Acéptalo para confirmar el partido!`;

                    await addDoc(collection(db, getCollectionPath('notifications')), {
                      userId: pid, type: 'match_reported',
                      title: 'Resultado Cargado',
                      body: customBodyStr,
                      matchId: match.id, read: false, createdAt: Date.now()
                    });
                  }
                }
              }

              if (initialMode === 'result') {
                onClose();
              } else {
                setIsReporting(false);
              }
            }}
            onCancel={match.reportedResult ? () => setIsReporting(false) : undefined}
          />
        )}

      </div>
    );
  };

  if (!t1 || !t2) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-2 sm:p-6 bg-black/30 backdrop-blur-sm" onClick={onClose} style={isReporting ? { isolation: 'isolate' } : undefined}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 15 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 15 }}
        transition={{ type: 'spring', damping: 25, stiffness: 350 }}
        className={`relative w-full mx-auto overflow-hidden flex flex-col shadow-2xl text-white ${
          isReporting 
            ? 'max-w-md bg-slate-800 border border-white/15 rounded-2xl max-h-[90vh]' 
            : 'max-w-xl max-h-[85vh] bg-slate-800 border border-white/20 rounded-2xl'
        }`}
        onClick={e => e.stopPropagation()}
      >
        
        {/* Header */}
        {isReporting ? (
          <div className="flex items-center justify-between p-4 border-b border-white/10 bg-black/20 shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl theme-primary-bg/15 flex items-center justify-center border border-white/10">
                <ShieldCheck size={18} className="theme-primary-text" />
              </div>
              <div>
                <h3 className="font-black text-white text-sm uppercase tracking-wider">Resultado Oficial</h3>
                <p className="text-[10px] text-zinc-500 font-medium uppercase tracking-wider">Carga el resultado</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {canViewChat && (
                <button onClick={() => setIsReporting(false)} className="px-2.5 py-1.5 bg-white/10 hover:bg-white/20 rounded-xl transition-all text-white font-black uppercase tracking-wider flex items-center gap-1.5 shadow-md border border-white/20 text-[9px]">
                  💬 Chat
                </button>
              )}
              <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-xl transition-colors text-zinc-400 hover:text-white">
                <X size={18} />
              </button>
            </div>
          </div>
        ) : (
          <div className="p-4 border-b border-white/5 flex items-center bg-white/[0.02] shrink-0 relative">
            <div className="flex-1 flex flex-col items-center justify-center text-center">
              <h2 className="text-sm font-black uppercase tracking-widest text-white/90 theme-font-secondary">
                Chat privado
              </h2>
              <span className="text-[10px] opacity-50 mt-1">
                Conversación privada. El organizador tendrá acceso caso de disputas.
              </span>
            </div>
            <button onClick={onClose} className="absolute right-4 top-1/2 -translate-y-1/2 p-2 hover:bg-white/10 rounded-full transition-colors text-white/50 hover:text-white">
              <X size={20} />
            </button>
          </div>
        )}

        <div className={`flex-1 overflow-y-auto flex flex-col p-4 gap-4 custom-scrollbar ${isReporting ? 'space-y-4' : ''}`}>
          {/* Teams Header */}
          {!isReporting && (
            <div className="flex items-center justify-between gap-2 pb-2 shrink-0">
               <div className="flex-1 flex flex-col items-center gap-0 text-center font-bold text-xs text-white/80">
                 <span>{formatPlayerName(players.find(p=>p.id===t1.player1Id)?.name||'','short')}</span>
                 {t1.player2Id && <span>{formatPlayerName(players.find(p=>p.id===t1.player2Id)?.name||'','short')}</span>}
               </div>
               <span className="text-xs font-black text-yellow-400 drop-shadow-[0_0_8px_rgba(250,204,21,0.5)] italic px-2 shrink-0">VS</span>
               <div className="flex-1 flex flex-col items-center gap-0 text-center font-bold text-xs text-white/80">
                 <span>{formatPlayerName(players.find(p=>p.id===t2.player1Id)?.name||'','short')}</span>
                 {t2.player2Id && <span>{formatPlayerName(players.find(p=>p.id===t2.player2Id)?.name||'','short')}</span>}
               </div>
            </div>
          )}

          {/* Action Panel */}
          {canViewChat && renderReportPanel()}

          {/* Chat System */}
          {!isReporting && (
            canViewChat ? (
            isNormalAdminLocked ? (
              <div className="flex-1 flex flex-col items-center justify-center bg-black/30 border border-white/5 rounded-2xl p-6 min-h-[320px] relative overflow-hidden group">
                {/* Cyberpunk ambient glow effect */}
                <div className="absolute -top-24 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl pointer-events-none group-hover:bg-amber-500/15 transition-all duration-700" />
                <div className="absolute -bottom-24 w-72 h-72 bg-theme-primary/5 rounded-full blur-3xl pointer-events-none" />

                <div className="relative z-10 flex flex-col items-center text-center max-w-md">
                  <div className="w-16 h-16 bg-gradient-to-b from-amber-500/20 to-yellow-500/5 rounded-2xl flex items-center justify-center mb-5 border border-amber-500/30 shadow-[0_0_20px_rgba(245,158,11,0.15)] group-hover:scale-105 transition-transform duration-500">
                    <Lock size={28} className="text-amber-500 animate-pulse" />
                  </div>

                  <h3 className="text-xs font-black uppercase tracking-widest text-amber-500 mb-2 theme-font-secondary">
                    Acceso Restringido al Chat
                  </h3>
                  <div className="w-12 h-[2px] bg-amber-500/30 rounded-full mb-4" />

                  <p className="text-xs text-white/80 leading-relaxed mb-4">
                    Como organizador (<span className="text-amber-400 font-bold">Admin</span>), no tienes permitido visualizar las conversaciones privadas entre los jugadores a menos que exista una disputa activa o resuelta.
                  </p>

                  <div className="p-3.5 bg-white/[0.02] border border-white/5 rounded-xl text-left flex items-start gap-3">
                    <EyeOff size={16} className="text-white/40 shrink-0 mt-0.5" />
                    <p className="text-[10px] text-white/50 leading-normal">
                      Una vez que se inicie una <span className="text-red-400 font-bold">Disputa</span> sobre el resultado del partido, se te habilitará automáticamente el acceso a todo el historial de chat para intervenir como mediador.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col bg-black/20 border border-white/5 rounded-2xl overflow-hidden min-h-[300px]">
                <div 
                  ref={containerRef}
                  onScroll={handleScroll}
                  className="flex-1 overflow-y-auto p-4 space-y-4"
                >
                  {messages.length > 0 && (
                    hasMore ? (
                      <div className="text-center py-2 text-[10px] uppercase tracking-widest text-white/30 font-bold animate-pulse">
                        ↾ Desliza hacia arriba para ver mensajes anteriores ↾
                      </div>
                    ) : (
                      <div className="text-center py-2 text-[10px] uppercase tracking-widest text-white/20 font-bold">
                        ✨ Inicio de la conversación — No hay más mensajes anteriores
                      </div>
                    )
                  )}
                  {messages.length === 0 && (
                    <div className="h-full flex flex-col items-center justify-center opacity-40 text-xs text-center font-medium gap-2">
                      <Send size={24} className="opacity-50" />
                      No hay mensajes aún.<br/>Usa este chat para arreglar horario y cancha.
                    </div>
                  )}
                  {messages.map(msg => {
                    const isMe = msg.senderId === user?.uid;
                    return (
                      <div key={msg.id} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                        <span className="text-[10px] opacity-40 mb-1 ml-1">{getPlayerName(msg.senderId)}</span>
                        <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${isMe ? 'theme-primary-bg text-black rounded-tr-sm shadow-md' : 'bg-white/10 text-white rounded-tl-sm'}`}>
                          {msg.text}
                        </div>
                        <span className="text-[8px] opacity-30 mt-1">{formatMessageDateTime(msg.createdAt)}</span>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
                {canSendMessage ? (
                  <div className="flex flex-col bg-black/40 border-t border-white/5">
                    {sedes.length > 0 && (
                      <div className="px-3 pt-2 relative">
                        {showReserveClubs ? (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setShowReserveClubs(false)} />
                            <div className="relative z-20 flex flex-col p-2 bg-white/5 rounded-xl animate-in fade-in slide-in-from-bottom-2">
                              <div className="flex gap-2 overflow-x-auto justify-center custom-scrollbar">
                                {sedes.length > 0 ? sedes.map(sede => (
                                  <button
                                    key={sede.id}
                                    onClick={async () => {
                                      const confirmed = await window.confirm(`Irás a la pestaña "reservas" de ${sede.name || 'este club'}`);
                                      if (confirmed) {
                                        localStorage.setItem('preselectedSedeId', sede.id);
                                        onClose();
                                        window.dispatchEvent(new CustomEvent('switch-to-reservas'));
                                      }
                                    }}
                                    className="flex flex-col items-center gap-1 min-w-[60px] max-w-[70px] p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                                  >
                                    {sede.photoUrl ? (
                                      <img src={sede.photoUrl} alt={sede.name} className="w-8 h-8 rounded-full object-cover border border-white/10 shrink-0" />
                                    ) : (
                                      <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center border border-white/10 shrink-0"><MapPin size={14} className="text-white/50"/></div>
                                    )}
                                    <span className="text-[8px] font-bold text-center w-full truncate text-white/80">{sede.name}</span>
                                  </button>
                                )) : (
                                  <span className="text-[10px] text-white/50 py-2 w-full text-center">No hay clubes asignados a este torneo</span>
                                )}
                              </div>
                            </div>
                          </>
                        ) : (
                          <button
                            onClick={() => setShowReserveClubs(true)}
                            className="w-full flex items-center justify-center gap-1.5 py-1.5 px-3 bg-white/[0.03] hover:bg-white/[0.08] border border-white/10 rounded-xl text-[10px] text-white/70 font-bold uppercase tracking-wider transition-all"
                          >
                            <span>📅</span> Reservar Cancha
                          </button>
                        )}
                      </div>
                    )}
                    <div className="p-3">
                      <form onSubmit={handleSendMessage} className="flex gap-2">
                        <input
                          type="text"
                          value={newMessage}
                          onChange={e => setNewMessage(e.target.value)}
                          placeholder={isAdmin ? "Mensaje como administrador..." : "Escribe un mensaje..."}
                          className="flex-1 bg-white/[0.03] hover:bg-white/[0.05] border border-white/10 focus:border-white/30 rounded-full px-4 py-2.5 text-sm outline-none transition-colors"
                        />
                        <button type="submit" disabled={!newMessage.trim()} className="w-10 h-10 flex items-center justify-center rounded-full theme-primary-bg text-black disabled:opacity-50 hover:opacity-90 transition-all active:scale-95 shadow-lg shrink-0">
                          <Send size={16} className="translate-x-0.5" />
                        </button>
                      </form>
                    </div>
                  </div>
                ) : isSuspended ? (
                  <div className="p-3 bg-red-500/10 border-t border-red-500/20 text-center py-4">
                    <p className="text-[10px] text-red-400 uppercase tracking-widest font-bold">🚫 Chat deshabilitado por cuenta inhabilitada</p>
                  </div>
                ) : isArchivedDispute ? (
                  <div className="p-3 bg-black/40 border-t border-white/5 text-center">
                    <p className="text-[10px] text-white/30 uppercase tracking-widest font-bold">🔒 Chat congelado — La disputa fue archivada</p>
                  </div>
                ) : isAdmin ? (
                  <div className="p-3 bg-black/40 border-t border-white/5 text-center">
                    <p className="text-[10px] text-white/30 uppercase tracking-widest font-bold">Chat de solo lectura — Intervención habilitada solo durante disputas</p>
                  </div>
                ) : null}
              </div>
            )
          ) : (
            <div className="flex-1 flex items-center justify-center opacity-40 text-center text-sm p-8">
              Chat habilitado solo para jugadores de este partido.
            </div>
          )
        )}
        </div>
      </motion.div>
    </div>
  );
}
